import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Loader2, Flag } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Enter the score after a league game. Parents are told straight away. */
export const FixtureResult = ({ fixture, onSaved }) => {
  const r = fixture.result || {};
  const [ours, setOurs] = useState(r.our_score ?? "");
  const [theirs, setTheirs] = useState(r.their_score ?? "");
  const [scorers, setScorers] = useState(r.scorers || "");
  const [busy, setBusy] = useState(false);

  const save = async (clear = false) => {
    if (!clear && (ours === "" || theirs === "")) {
      toast.error("Both scores please");
      return;
    }
    setBusy(true);
    try {
      const res = await axios.post(`${API}/academy/fixtures/${fixture.id}/result`,
        clear ? { clear: true }
              : { our_score: Number(ours), their_score: Number(theirs), scorers, notify: true },
        { withCredentials: true });
      toast.success(clear
        ? "Score cleared"
        : `Saved · ${res.data.parents_notified} parent(s) told · ${res.data.record.points} pts`);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save the score");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-3 rounded-lg border border-slate-700 bg-slate-950/80 p-3"
      data-testid={`fixture-result-panel-${fixture.id}`}>
      <p className="text-xs text-slate-400 mb-2 flex items-center gap-1">
        <Flag className="w-3 h-3 text-emerald-400" /> Final score
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <Input type="number" min="0" max="99" value={ours} onChange={(e) => setOurs(e.target.value)}
          className="h-10 w-16 bg-slate-900 border-slate-700 text-center"
          data-testid={`result-ours-${fixture.id}`} />
        <span className="text-slate-500 text-xs">us · them</span>
        <Input type="number" min="0" max="99" value={theirs} onChange={(e) => setTheirs(e.target.value)}
          className="h-10 w-16 bg-slate-900 border-slate-700 text-center"
          data-testid={`result-theirs-${fixture.id}`} />
        <Input value={scorers} onChange={(e) => setScorers(e.target.value)}
          placeholder="Scorers (optional)"
          className="h-10 flex-1 min-w-[160px] bg-slate-900 border-slate-700"
          data-testid={`result-scorers-${fixture.id}`} />
        <Button size="sm" className="h-10 bg-emerald-600 hover:bg-emerald-500 text-[11px]"
          onClick={() => save(false)} disabled={busy} data-testid={`result-save-${fixture.id}`}>
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : "Save score"}
        </Button>
        {r.our_score !== undefined && r.our_score !== null && (
          <Button size="sm" variant="ghost" className="h-10 text-slate-400 text-[11px]"
            onClick={() => save(true)} disabled={busy} data-testid={`result-clear-${fixture.id}`}>
            Clear
          </Button>
        )}
      </div>
      {r.entered_by_name && (
        <p className="text-[11px] text-slate-500 mt-2">
          Entered by {r.entered_by_name}
          {r.entered_at ? ` on ${new Date(r.entered_at).toLocaleDateString()}` : ""}
        </p>
      )}
    </div>
  );
};
